// Person detail (prototype renderDetail): confirmation per registration, error + what to do, conversation,
// reply and react via wa_reply, reply-window ring, timeline. Opening it resets unread (the only Firestore write).
import {
  ArrowClockwise,
  ArrowUp,
  CaretDown,
  CaretLeft,
  CaretUp,
  Check,
  CheckCircle,
  Checks,
  Clock,
  Copy,
  DotsThreeOutline,
  Eye,
  FileXls,
  HourglassMedium,
  Lock,
  PaperPlaneTilt,
  type Icon as PhIcon,
  Phone,
  Prohibit,
  SealCheck,
  Timer,
  Users,
  WarningCircle,
  Wrench,
  X,
} from "@phosphor-icons/react";
import { type CSSProperties, Fragment, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Av, Ic, JourneyDots, StatusBadge, useTick, WinChip } from "@/components/bits";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { markRead, sendReaction, sendReply, useThread } from "@/data/queries";
import type { ThreadMessage, WaConfig } from "@/data/types";
import { DAY_MS } from "@/lib/fields";
import { ago, clock, dayClock, dayLabel, dur, formatPhone } from "@/lib/format";
import { explainError, todo } from "@/model/advice";
import { windowLeft, windowOpen } from "@/model/cap";
import { isWaiting } from "@/model/overview";
import { type Person, personError, type Reg } from "@/model/people";
import { canReact, nextReaction, REACTIONS, threadBubbles } from "@/model/reactions";
import { batchOf, type RunRow } from "@/model/runs";
import { renderParts, templateBody, varsFor } from "@/model/templates";
import { useApp } from "@/shell/appData";
import { copyText, useToast } from "@/shell/toast";
import { batchLabel } from "./PersonRow";

const QUICK = ["We will check and get back to you", "Thank you! See you at the event 🙏"];

function TemplateText({
  name,
  vars,
  config,
}: {
  name: string | null;
  vars: Record<string, string>;
  config: WaConfig | null;
}) {
  const body = templateBody(name, config);
  if (!body) return <span className="muted">…</span>;
  return (
    <>
      {renderParts(body, vars).map((x, i) =>
        x.v ? (
          <span key={i} className="var">
            {x.text}
          </span>
        ) : (
          <Fragment key={i}>{x.text}</Fragment>
        ),
      )}
    </>
  );
}

function Ticks({ status }: { status: string | null }) {
  if (status === "read") return <Ic i={Checks} w="bold" className="rd" />;
  if (status === "delivered") return <Ic i={Checks} w="bold" />;
  if (status === "sent") return <Ic i={Check} w="bold" />;
  if (status === "failed") return <Ic i={WarningCircle} w="bold" style={{ color: "var(--destructive)" }} />;
  return <Ic i={Clock} />;
}

function Ring({ frac, color, size = 44 }: { frac: number; color: string; size?: number }) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const [off, setOff] = useState(c);
  useEffect(() => {
    const t = requestAnimationFrame(() => setOff(c * (1 - frac)));
    return () => cancelAnimationFrame(t);
  }, [c, frac]);
  return (
    <svg className="wring" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} className="rbg" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        className="rfg"
        style={{ stroke: color, strokeDasharray: c, strokeDashoffset: off }}
      />
    </svg>
  );
}

type Step = { cls: string; icon: PhIcon; title: string; sub?: string; when?: number | null };

function timeline(p: Person, planned: number | null, rows: RunRow[]): Step[] {
  const r = p.primary;
  const row = r?.ledger;
  const n = p.regs.length;
  const out: Step[] = [];
  if (p.regs.some((x) => x.inSheet)) {
    out.push({
      cls: "done",
      icon: FileXls,
      title: "Imported from sheet",
      sub: `${n} registration${n > 1 ? "s" : ""} on this phone`,
      // sheet_ts = when the form row was submitted (naive local ISO from the sheet)
      when: r?.sheetTs ? Date.parse(r.sheetTs) || null : null,
    });
  } else if (n) {
    out.push({ cls: "warn", icon: FileXls, title: "No longer in the sheet", sub: "Messaged before it left" });
  }
  // The sender only retries rows still in the sheet, so these never move again.
  for (const x of p.regs) {
    if (!x.notRetried) continue;
    out.push({
      cls: "warn",
      icon: X,
      title: `${x.status === "sending" ? "Stuck sending" : "Failed"} · not retried · ${x.key}`,
      sub: `No longer in the sheet${x.ledger?.error?.title ? ` · ${x.ledger.error.title}` : ""}`,
      when: x.ledger?.failedAt ?? x.ledger?.lastAttemptAt ?? null,
    });
  }
  if (!r || p.status === "not_on_list") return out;
  if (p.status === "queued") {
    out.push({
      cls: "",
      icon: HourglassMedium,
      title: "Not sent yet",
      sub: planned ? `Planned for batch ${planned} · goes with the next send run` : "Goes with a later send run",
    });
    return out;
  }
  if (p.status === "invalid") {
    out.push({ cls: "bad", icon: X, title: "Skipped before sending", sub: personError(p)?.title || "Invalid phone" });
    return out;
  }
  const handed = row?.sentAt ?? row?.lastAttemptAt ?? null;
  const b = batchOf(handed, rows);
  out.push({
    cls: "done",
    icon: PaperPlaneTilt,
    title: "Handed to WhatsApp",
    sub: `${b ? `${b.label} · ` : ""}${n} message${n > 1 ? "s" : ""}`,
    when: handed,
  });
  if (p.status === "sending") {
    out.push({ cls: "", icon: Clock, title: "Sending", sub: "Waiting for WhatsApp to accept · check `wa pending`" });
    return out;
  }
  if (p.status === "retrying" || p.status === "failed" || p.status === "over_limit") {
    const tries = Math.max(row?.attempts ?? 1, 1);
    out.push({
      cls: p.status === "failed" ? "bad" : "warn",
      icon: X,
      title: p.status === "over_limit" ? "Over the 24h limit" : tries > 1 ? `Try ${tries} failed` : "Failed",
      sub:
        p.status === "retrying"
          ? "The next send run tries again"
          : p.status === "over_limit"
            ? "Waiting for Retry all on Overview"
            : (row?.error?.title ?? ""),
      when: row?.failedAt,
    });
    return out;
  }
  const isDel = p.status === "delivered" || p.status === "read" || p.status === "opted_out";
  out.push({
    cls: isDel ? "done" : "",
    icon: Checks,
    title: "Delivered",
    sub: isDel ? "On their phone" : "Waiting for their phone to come online",
    when: isDel ? (row?.deliveredAt ?? null) : null,
  });
  if (p.status === "opted_out") {
    out.push({ cls: "bad", icon: Prohibit, title: "Replied STOP", sub: "We won’t message them again" });
    return out;
  }
  out.push({
    cls: p.status === "read" ? "done" : "",
    icon: Eye,
    title: "Read",
    sub: p.status === "read" ? "Blue ticks" : "Not opened yet",
    when: p.status === "read" ? (row?.readAt ?? null) : null,
  });
  return out;
}

/** Which registration a thread template message belongs to. */
function regFor(m: ThreadMessage, p: Person): Reg | undefined {
  return p.regs.find((r) => r.key === m.regId || (m.ledgerPath ? m.ledgerPath.endsWith(`/${r.key}`) : false));
}

export function Detail({
  p,
  dir,
  now,
  planned,
  onClose,
  onNav,
}: {
  p: Person;
  dir: number;
  now: number;
  planned: number | null;
  onClose: () => void;
  onNav: (d: number) => void;
}) {
  const { config, runRows } = useApp();
  const toast = useToast();
  const tick = useTick(30_000);
  const t = Math.max(now, tick);
  const thread = useThread(p.conv && p.phone ? p.phone : null);
  const msgs = threadBubbles(thread.data ?? []);
  const chat = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  // One wa_reply per submit: Enter, a double click or a held Enter can all call send() before React re-renders.
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [err, setErr] = useState<string | null>(null);
  // Reaction picker: hover shows it on desktop (CSS); a tap pins it open for one bubble on touch screens.
  const [pickFor, setPickFor] = useState<string | null>(null);
  const [reacting, setReacting] = useState<string | null>(null);

  // Opening a thread resets unread (as the old inbox does). "Replies waiting" drops on open, not on send.
  const unread = p.conv?.unread ?? 0;
  useEffect(() => {
    if (unread > 0 && p.phone) markRead(p.phone).catch(() => {});
  }, [unread, p.phone]);

  // keep the newest message in view
  const lastId = msgs[msgs.length - 1]?.id;
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll when a message arrives
  useLayoutEffect(() => {
    const el = chat.current;
    if (el && window.innerWidth >= 1280) el.scrollTop = el.scrollHeight;
  }, [lastId]);

  const error = personError(p);
  const ex = explainError(p, error);
  const td = todo(p, planned);
  const replied = !!p.conv?.lastInboundAt;
  const left = windowLeft(p.conv, t);
  const open = windowOpen(p.conv, p.optedOut, t);
  const notSent = p.status === "queued" || p.status === "invalid";

  const send = async () => {
    if (inFlight.current) return;
    const body = text.trim();
    if (!body || !p.phone) {
      box.current?.focus();
      return;
    }
    inFlight.current = true;
    const name = p.name;
    setSending(true);
    setErr(null);
    try {
      await sendReply(p.phone, body);
      if (mounted.current) {
        setText("");
        box.current?.focus({ preventScroll: true });
      }
      toast("Reply sent");
    } catch (e) {
      const reason = (e as Error).message || "Send failed";
      // app-level, so it still shows if the admin has already left this thread
      toast(`Reply to ${name} failed: ${reason}`);
      if (mounted.current) setErr(reason);
    } finally {
      inFlight.current = false;
      if (mounted.current) setSending(false);
    }
  };

  const react = async (m: ThreadMessage, picked: string) => {
    if (reacting || !p.phone) return;
    setPickFor(null);
    setReacting(m.id);
    try {
      await sendReaction(p.phone, m.id, nextReaction(m.ourReaction, picked));
    } catch (e) {
      toast(`Reaction failed: ${(e as Error).message || "send failed"}`);
    } finally {
      if (mounted.current) setReacting(null);
    }
  };

  // ---------------------------------------------------------------- chat body
  const bubbles: ReactNode[] = [];
  const sentRegs = new Set(msgs.map((m) => regFor(m, p)?.key).filter(Boolean));
  const unsent = p.regs.filter((r) => !sentRegs.has(r.key) && !r.ledger?.wamid);
  if (unsent.length && (notSent || !msgs.length || p.status === "retrying")) {
    bubbles.push(
      <div className="daysep" key="plan">
        {p.status === "queued"
          ? `Planned${planned ? ` · batch ${planned}` : ""}`
          : p.status === "invalid"
            ? "Not sent · the number needs a fix"
            : "Not sent yet"}
      </div>,
    );
    for (const r of unsent) {
      bubbles.push(
        <div className="bub out dim" key={`plan-${r.key}`}>
          <div className="tg">
            <Ic i={SealCheck} />
            {config?.template ?? r.ledger?.template ?? "template"} · {r.key}
          </div>
          <TemplateText
            name={config?.template ?? r.ledger?.template ?? null}
            vars={varsFor(r, config)}
            config={config}
          />
          <div className="bm">
            <Ic i={Clock} />
          </div>
        </div>,
      );
    }
  }
  let lastDay = "";
  for (const m of msgs) {
    const day = m.at ? new Date(m.at).toDateString() : "";
    if (day && day !== lastDay) {
      bubbles.push(
        <div className="daysep" key={`d-${m.id}`}>
          {dayLabel(m.at!)}
          {!lastDay && m.kind === "template" ? ` · ${clock(m.at!)}` : ""}
        </div>,
      );
      lastDay = day;
    }
    if (m.kind === "template") {
      const r = regFor(m, p);
      bubbles.push(
        <div className={`bub out${m.status === "failed" ? " fail" : ""}`} key={m.id} data-kind="template">
          <div className="tg">
            <Ic i={SealCheck} />
            {m.template} · {m.regId ?? r?.key}
          </div>
          <TemplateText name={m.template} vars={r ? varsFor(r, config) : {}} config={config} />
          <div className="bm">
            {m.at ? clock(m.at) : ""} <Ticks status={m.status} />
          </div>
        </div>,
      );
    } else if (m.dir === "out") {
      bubbles.push(
        <div className="bub out" key={m.id} data-kind="reply">
          {m.kind ? m.text : <span className="muted">(message sent elsewhere)</span>}
          <div className="bm">
            {m.sentBy && m.sentBy !== "runner" ? `${m.sentBy.split("@")[0]} · ` : ""}
            {m.at ? clock(m.at) : ""} <Ticks status={m.status} />
          </div>
        </div>,
      );
    } else {
      const pickable = open && canReact(m);
      const cls = `bub in${pickable ? " rx-ok" : ""}${pickFor === m.id ? " picking" : ""}${m.ourReaction ? " reacted" : ""}`;
      bubbles.push(
        <div
          className={cls}
          key={m.id}
          data-kind="in"
          onPointerUp={
            pickable
              ? (e) => {
                  if (e.pointerType === "touch") setPickFor((x) => (x === m.id ? null : m.id));
                }
              : undefined
          }
        >
          {m.text || <span className="muted">({m.kind ?? "message"})</span>}
          <div className="bm">{m.at ? clock(m.at) : ""}</div>
          {pickable ? (
            <div className="rx-pick" role="toolbar" aria-label="React">
              {REACTIONS.map((x) => (
                <button
                  key={x}
                  type="button"
                  className={m.ourReaction === x ? "on" : undefined}
                  aria-label={m.ourReaction === x ? `Remove ${x}` : `React ${x}`}
                  aria-pressed={m.ourReaction === x}
                  disabled={!!reacting}
                  onPointerUp={(e) => e.stopPropagation()}
                  onClick={() => react(m, x)}
                >
                  {x}
                </button>
              ))}
            </div>
          ) : null}
          {m.ourReaction ? (
            <span className={`rx-badge${reacting === m.id ? " busy" : ""}`} data-testid="our-reaction">
              {m.ourReaction}
            </span>
          ) : null}
        </div>,
      );
    }
  }
  if (msgs.some((m) => m.kind === "template") || unsent.length) {
    bubbles.push(
      <div className="cap-note" key="note">
        Highlighted parts come from the sheet
      </div>,
    );
  }
  if (!replied && !notSent && p.status !== "failed" && p.status !== "retrying" && p.status !== "over_limit") {
    bubbles.push(
      <div className="daysep" style={{ marginTop: 18 }} key="none">
        No replies yet
      </div>,
    );
  }

  const ringColor = !open ? "var(--tertiary)" : left < 3 * 3600e3 ? "var(--st-retry)" : "var(--st-read)";
  const style = {
    "--dy": `${dir === 1 ? 8 : dir === -1 ? -8 : 0}px`,
    "--dx": `${dir === 2 ? 8 : 0}px`,
  } as CSSProperties;

  return (
    <div className="dt-in a" style={style}>
      <div className="dt-h">
        <button type="button" className="btn ghost icon back-btn" aria-label="Back to list" onClick={onClose}>
          <Ic i={CaretLeft} />
        </button>
        <Av name={p.name} lg />
        <div className="t">
          <h2 data-testid="detail-name">
            {p.name}
            {p.regs.length > 1 ? (
              <span className="fam">
                <Ic i={Users} />+{p.regs.length - 1}
              </span>
            ) : null}
          </h2>
          <div className="s">
            <span className="mono">{formatPhone(p.phone)}</span>
            {p.regs.length ? (
              <>
                {" · "}
                <span className="mono">{p.regs.map((r) => r.key).join(", ")}</span>
              </>
            ) : null}
          </div>
          <div className="bd">
            <JourneyDots s={p.status} />
            <StatusBadge s={p.status} />
            <span className="badge soft">{batchLabel(p, planned, runRows)}</span>
            {isWaiting(p) ? <WinChip p={p} now={t} /> : null}
          </div>
        </div>
        <div className="dt-acts">
          <button
            type="button"
            className="btn ghost icon hide-sm"
            aria-label="Previous person"
            title="Previous (k)"
            onClick={() => onNav(-1)}
          >
            <Ic i={CaretUp} />
          </button>
          <button
            type="button"
            className="btn ghost icon hide-sm"
            aria-label="Next person"
            title="Next (j)"
            onClick={() => onNav(1)}
          >
            <Ic i={CaretDown} />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="btn ghost icon" aria-label="More actions">
                <Ic i={DotsThreeOutline} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="pop rdx" style={{ minWidth: 240 }}>
              <DropdownMenuLabel className="pl">{p.name}</DropdownMenuLabel>
              {p.regs[0] ? (
                <DropdownMenuItem
                  onSelect={() => {
                    const id = p.regs.map((r) => r.key).join(", ");
                    copyText(id).then((ok) => toast(ok ? `Copied ${id}` : "Could not copy"));
                  }}
                >
                  <Ic i={Copy} />
                  Copy reg ID
                </DropdownMenuItem>
              ) : null}
              {p.phone ? (
                <DropdownMenuItem asChild>
                  <a href={`tel:+${p.phone}`}>
                    <Ic i={Phone} />
                    Call {formatPhone(p.phone)}
                  </a>
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            type="button"
            className="btn ghost icon close-btn"
            aria-label="Close details"
            title="Close (Esc)"
            onClick={onClose}
          >
            <Ic i={X} />
          </button>
        </div>
      </div>
      <div className="dt-body">
        <div className="dt-main">
          {ex || p.status === "queued" ? (
            <div className="dt-alert">
              {ex ? (
                <div className={`err${ex.retrying ? " retry" : ""}`} data-testid="detail-error">
                  <div className="eh">
                    <Ic i={ex.retrying ? ArrowClockwise : WarningCircle} w="fill" />
                    {ex.title}
                  </div>
                  {ex.detail ? <p>{ex.detail}</p> : null}
                  <div className="code">{ex.code}</div>
                </div>
              ) : null}
              {p.status === "queued" ? (
                <div className="note">
                  <Ic i={HourglassMedium} />
                  <div>
                    <b>Not sent yet.</b> {planned ? `Planned for batch ${planned}. ` : ""}Nothing to do — it goes with
                    the next send run the daily cap allows.
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="dt-chat" ref={chat}>
            <div className="msgs" data-testid="thread">
              {bubbles}
            </div>
          </div>
          {replied ? (
            <div className="th-f">
              {open ? (
                <>
                  {left < 3 * 3600e3 ? (
                    <div className="note warn">
                      <Ic i={Timer} />
                      <div>
                        <b>{dur(left)} left to reply.</b> After that, only an approved template can be sent.
                      </div>
                    </div>
                  ) : null}
                  <div className="quick">
                    {QUICK.map((x) => (
                      <button
                        key={x}
                        type="button"
                        onClick={() => {
                          setText(x);
                          box.current?.focus();
                        }}
                      >
                        {x}
                      </button>
                    ))}
                  </div>
                  <div className="compose">
                    <textarea
                      ref={box}
                      rows={1}
                      value={text}
                      maxLength={4096}
                      placeholder="Reply on WhatsApp…"
                      aria-label="Reply"
                      onChange={(e) => {
                        setText(e.target.value);
                        e.target.style.height = "auto";
                        e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          send();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn primary send"
                      aria-label="Send reply"
                      disabled={sending || !text.trim()}
                      onClick={send}
                    >
                      <Ic i={ArrowUp} w="fill" />
                    </button>
                  </div>
                  {err ? (
                    <div className="note bad" role="alert">
                      <Ic i={WarningCircle} />
                      <div>{err}</div>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="note">
                  <Ic i={Lock} />
                  <div>
                    {p.optedOut ? (
                      <>
                        <b>They opted out.</b> They replied STOP, so we can’t message them unless they write to us
                        again.
                      </>
                    ) : (
                      <>
                        <b>Reply window closed {ago((p.conv?.lastInboundAt ?? 0) + DAY_MS, t)}.</b> Free replies are
                        allowed only within 24 hours of their last message.
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
        <aside className="dt-side">
          {replied ? (
            <div>
              <div className="label">Reply window</div>
              <div className="wbox">
                <Ring frac={open ? Math.max(0, Math.min(1, left / DAY_MS)) : 0} color={ringColor} />
                <div>
                  <b data-testid="window-left">{open ? dur(left) : p.optedOut ? "Opted out" : "Closed"}</b>
                  <span>{open ? "left · free text allowed" : "template only"}</span>
                </div>
              </div>
            </div>
          ) : null}
          <div>
            <div className="label">What to do</div>
            <div className="side-todo">
              <Ic
                i={td.act ? Wrench : CheckCircle}
                w="fill"
                style={td.act ? { color: "var(--destructive)" } : undefined}
              />
              <span className={`todo ${td.act ? "act" : "none"}`}>{td.text}</span>
            </div>
          </div>
          <div>
            <div className="label">Timeline</div>
            <ul className="tl">
              {timeline(p, planned, runRows).map((s) => (
                <li key={s.title} className={s.cls}>
                  <span className="td">
                    <Ic i={s.icon} w="bold" />
                  </span>
                  <span>
                    <div className="tt">{s.title}</div>
                    {s.sub ? <div className="ts">{s.sub}</div> : null}
                  </span>
                  <span className="tw">{s.when ? dayClock(s.when) : ""}</span>
                </li>
              ))}
            </ul>
          </div>
          {p.regs.length ? (
            <div>
              <div className="label">
                On this phone · {p.regs.length} registration{p.regs.length > 1 ? "s" : ""}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {p.regs.map((r) => (
                  <div className="regc" key={r.key}>
                    <b>{r.name}</b>
                    <div className="mono muted" style={{ fontSize: 11 }}>
                      {r.key}
                      {r.inSheet ? "" : " · not in the sheet any more"}
                    </div>
                    {r.days || r.dates ? (
                      <div className="muted">
                        {r.days ? `${r.days} day${r.days === "1" ? "" : "s"}` : ""}
                        {r.days && r.dates ? " · " : ""}
                        {r.dates}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
